/**
 * This file is meant to contain the bulk of the business and data logic of
 * promotions -- particularly promotions that are configured via configuration.json's
 * `PromotionInterval`s
 */

import { getBookingConfig } from "../data/price"
import {isWithinInterval, parseISO} from 'date-fns'
import { bookingConfig, DeliveryPromo, DeliveryTypePromoMatrix, ExtraItemSliceInterface, FormType, FreeItem, gunOptionsInterface, GunTypePromoMatrix, JsonInterval, PromoMatrix, PromotionInterval } from "../types/types";
import { DeliveryManager } from "../data/DeliveryManager";
import { gunKeyFromGunType } from "../components/utils/stateHelper";

/**
 * Quick check of whether or not a date is in any promotion interval in the config.
 */
export function isInPromoInterval(date: Date, lang: string, formType: FormType) {
    console.log("Checking promo date", date);
    const promo = whichPromoInterval(date, lang, formType);
    if (promo === false) {
        return false;
    } else {
        return true;
    }

}

/**
 * Returns the JSON object of the promotion interval the date is in or false
 * E.g. "Kerst vakantie" (Christmas vacation)
 */
export function whichPromoInterval(date: Date, lang: string, formType: FormType): PromotionInterval | false {
    const config = getBookingConfig();
    const manager = new DeliveryManager(config);
    const intervals = config.promoIntervals;
    console.log(intervals);
    
    // In order to use the JsonInterval functions, we have to pretend that the object may not have
    // the PromotionInterval properties, but it will always be a PromotionInterval
    const index = intervals.findIndex((interval: JsonInterval | PromotionInterval) => {
        console.log(interval);
        // If languages is defined, then the language must be in the list of languages for the promotion
        if ('languages' in interval && interval.languages && !interval.languages.includes(lang)) {
            return false;
        }
        return isWithinInterval(
            date,
            {
                start: parseISO(interval.start),
                end:   parseISO(interval.end)
            }
        );
    });

    if (index === -1) {
        return false;
    }

    const promo = intervals[index];
    if (promo && promo.customCodeHooks && promo.customCodeHooks.includes("youthCampOnly")) {
        if (formType != "YouthPromo") {
            return false;
        }
    }
    return promo;
}

/**
 * Returns the number of days to use as the range in the setPlayRange redux function. Assuming that
 * the playStart is in a promo interval, the promotion play range will be calculated. If the config
 * of the promotion has the "extension" field set, then two things are possible.
 * 
 * extension === true          - means that it's from playStart to the end of the interval
 * typeof extension === number - means that it's from playStart to that many days
 * 
 * Else, just one day
 */
export function promoPlayRange(playStart: Date, lang: string, formType: FormType) {
    const promo = whichPromoInterval(playStart, lang, formType);
    console.log("Promo", promo);
    if (promo === false) {
        console.error("Thought date was in promo period, but other function disagrees");
    } else {
        const config = getBookingConfig();
        if ('extension' in promo) {
            if (promo.extension === true) {
                // @ts-ignore
                const promoEnd = parseISO(promo.end);
                const millisecondsBetween = promoEnd.getTime() - playStart.getTime();
                const millisecondsInADay = 1000*60*60*24;
                const dayDifference = millisecondsBetween / millisecondsInADay;
                console.log("End", promoEnd);
                console.log("Difference", dayDifference);
                return Math.floor(dayDifference + 2);            
            } else if (typeof promo.extension === 'number') {
                return promo.extension;
            } else {
                return 1;
            }
        } else {
            return 1;
        }
    }
}

/**
 * UTIL
 * 
 * Returns an array of dates between (and including) the start and end date
 * 
 * Used for highlighting the dates between playStart and playEnd.
 */
export function getDaysArray(inpStart: Date, inpEnd: Date) {
    // console.log(inpStart);
    // console.log(inpEnd);
    const arr = [];
    if (inpStart && inpEnd) {
        const start = new Date(inpStart).setUTCHours(0, 0, 0 ,0);
        const end = new Date(inpEnd).setUTCHours(0,0,0,0);
        for(var dt=new Date(start); dt.toISOString() <= new Date(inpEnd).toISOString(); dt.setDate(dt.getDate()+1)){
            // console.log(dt.toISOString());
            arr.push(new Date(dt));
            const nextDate = new Date(dt);
            nextDate.setDate(dt.getDate()+1);
            // console.log("Next: " + nextDate.toISOString());
            // console.log(inpEnd.toISOString());
            // console.log(nextDate <= inpEnd);
        }
    }
    console.log(arr);
    return arr;
};

/**
 * Goal: Find the delivery promotion with the strictest requirements that can be met.
 *       These will be the "best" promotions.
 * 
 * For now, this is limited to highest min quantity, but custom code hooks can be added.
 */
export function bestDeliveryPromo(deliveryPromos: DeliveryPromo[], gunType: string, gunQuantity: number) {
    console.log("bestDeliveryPromo", [deliveryPromos, gunType, gunQuantity]);
    let deliveryPromo: DeliveryPromo | null = null;
    let highestMinQuantity = 0;
    for (let i = 0; i < deliveryPromos.length; i++) {
        const possiblePromo = deliveryPromos[i];
        console.log("Possible promo", possiblePromo);
        // Ignore promo if not enough gun quantity or if exceeds max quantity
        if (
            (possiblePromo.minQuantity === undefined || possiblePromo.minQuantity <= gunQuantity)
            && (possiblePromo.maxQuantity === undefined || possiblePromo.maxQuantity >= gunQuantity)
        ) {
            // Only replace promo if "better" minQuantity
            if (
                deliveryPromo === null
                || (possiblePromo.minQuantity && possiblePromo.minQuantity > highestMinQuantity)
            ) {
                deliveryPromo = possiblePromo;
                highestMinQuantity = possiblePromo.minQuantity || 0;
            }
        }
    }
    console.log("bestDeliveryPromo result", deliveryPromo);
    return deliveryPromo;
}

/**
 * Checks whether or not the promotion interval has options that fit the gun options
 * Whether the playStart falls within the interval is a different check.
 */
export function promoIntervalRelevantToGunOptions(promoInterval: PromotionInterval, gunOptions: gunOptionsInterface, extras: ExtraItemSliceInterface): boolean {
    // Promo interval without specific promotion -- ignore this case for now
    if (promoInterval.promotion === undefined) {
        // TODO: Handle
        return false;
    }

    // Step 1, check that there is a promotion for the gunType
    let gunKey = gunKeyFromGunType(gunOptions.gunType!, promoInterval.promotion);
    if (gunKey === undefined) {
        return false;
    }


    const deliveryMethods = Object.values(promoInterval.promotion[gunKey as keyof GunTypePromoMatrix]!);
    // Using 'find' instead of 'some' for debug purposes
    const promoMatrix = deliveryMethods.find((promoMatrix: PromoMatrix) => {
        let hasRelevantPromo = false;
        // Check if any delivery promos apply
        if (promoMatrix.deliveryPromos) {
            hasRelevantPromo = promoMatrix.deliveryPromos.some((deliveryPromo: DeliveryPromo) => {
                console.log("Checking delivery promo", deliveryPromo);
                
                /* Exclusion cases */

                // Check that the quantity meets the requirement
                if (
                    deliveryPromo.minQuantity
                    && deliveryPromo.minQuantity > gunOptions.quantity!
                ) {
                    console.log("Doesn't meet min quantity", [deliveryPromo.minQuantity, gunOptions.quantity!]);
                    return false;
                }

                console.log("Found a delivery promo");
                // If all checks passed, return true
                return true;
            });
        }
        if (hasRelevantPromo) {
            return true;
        }

        if (promoMatrix.freeItems) {
            // Check each free item entry
            hasRelevantPromo = promoMatrix.freeItems.some((item: FreeItem) => {
                if (item.minQuantity) {
                    return item.minQuantity <= gunOptions.quantity!;
                } else {
                    // Custom code hooks go here
                    if (item.customCodeHooks !== undefined) {
                        if (item.customCodeHooks.includes('freeDeliverySwitch') && !extras.freeDeliverySwitch) {
                            return false;
                        }
                    }
                    return true;
                }
            });
        }

        if (hasRelevantPromo) {
            return true;
        } else {
            return false;
        }
    });
    console.log("Promo matrix", promoMatrix);
    return promoMatrix !== undefined;
}


